import { usePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The wizard's "When are you open?" week — design sync 2026-10-09 (`design/WeTalk.dc.html`, Agent setup, the `wh`
 * block). One card: the time zone, then a row per day — its short name, an Open/Closed switch, and its shifts as
 * `HH:MM` boxes with "Add hours" (up to three) — and "Copy to all open days" on the first open day, which says what it
 * did and can be undone.
 *
 * AMENDED 2026-10-09 (plan-015 AGC-UI, gaps G4 and G12). What the design sync could only report is real now: the time zone
 * is the VoiceAgent's OWN (owner answer 12 — the workspace's until changed) and the select changes it; "Special dates" —
 * a single date closed or with its own hours — is drawn under the week as the prototype draws it (chips with remove,
 * consecutive dates sharing a chip, the DD/MM/YYYY box, Closed / Own hours, "Add date"). Both travel to the build as
 * `time_zone` and `special_date[]` (IC-DG-2). The same file draws the pieces of "Out of hours" and "When every line is
 * busy" that Agent setup and the agent page's "When it answers" card share ({@link AnsweringChoiceGroup},
 * {@link LongestWaitField}, {@link ForwardNumberField}), so the two surfaces say the same thing about the same answer.
 *
 * AMENDED 2026-10-10 (design sync of the export of 2026-10-10): the time zone select names each zone as the export does
 * ("Rome time (Italy)", `timeZoneLabelOf`; the value stays the IANA name); in Agent setup the dates under the week are
 * "Holidays and special days" (the agent page keeps "Special dates", as the export words each), both with "Days you're
 * closed or open different hours.", and "That day" is Closed / Different hours.
 *
 * The edit panel keeps `./opening-hours-editor.tsx`; this editor writes the same `OpeningWeek`.
 */
import { Icon } from "@wetalk/ui";
import { useId, useRef, useState, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import {
  DAY_NAME,
  DAY_SHORT,
  FORWARD_EXAMPLE_NUMBER,
  FRESH_SPECIAL_DATE,
  MAX_SHIFT_PER_DAY,
  WAIT_MINUTE_CHOICE,
  specialDateChipOf,
  specialDateOfDraft,
  timeZoneLabelOf,
  timeZoneOptionOf,
  withSpecialDate,
  withoutSpecialDate,
  type AnsweringOption,
  type OpeningPeriod,
  type OpeningWeek,
  type SpecialDateDraft,
  type SpecialDateEntry,
  type WaitMinute,
} from "./opening-hours.js";
import "./answering.css";

/** The special dates the week editor draws under the week, and where a change goes. */
export interface SpecialDateBinding {
  readonly entry: readonly SpecialDateEntry[];
  /** Today in the VoiceAgent's zone (`YYYY-MM-DD`): an earlier date is refused, since the build would drop it. */
  readonly today: string;
  readonly onEntry: (entry: readonly SpecialDateEntry[]) => void;
}

export interface OpeningWeekEditorProps {
  readonly id: string;
  readonly week: OpeningWeek;
  /** The VoiceAgent's own time zone (the workspace's until changed); `null` while it is not known. */
  readonly timeZone: string | null;
  readonly describedBy: string | undefined;
  readonly invalid: boolean | undefined;
  readonly onWeek: (week: OpeningWeek) => void;
  /** The week's accessible name, where no visible label names it (Agent setup draws none since 2026-10-10). */
  readonly label?: string;
  /** A zone chosen in the select. Absent, the select shows the zone and changes nothing. */
  readonly onTimeZone?: (timeZone: string) => void;
  /** The special dates, drawn under the week when given (plan-015 G4). */
  readonly specialDate?: SpecialDateBinding;
}

/** A shift added to a day: blank for a day opened afresh, from the last shift's close otherwise (the prototype's). */
const BLANK_SHIFT: OpeningPeriod = { opensAt: "", closesAt: "" };

const SHIFT_ORDINAL = () => (["", portalText("operations", "second_shift_ca87598b"), portalText("operations", "third_shift_2619f601")] as const);

interface Copied {
  readonly from: string;
  readonly count: number;
  readonly previous: OpeningWeek;
}

export function OpeningWeekEditor({
  id,
  week,
  timeZone,
  label,
  describedBy,
  invalid,
  onWeek,
  onTimeZone,
  specialDate,
}: OpeningWeekEditorProps): ReactNode {
  usePortalTranslation("operations");
  const [copied, setCopied] = useState<Copied | null>(null);
  // A day closed and opened again gets its shifts back, as the prototype's switch keeps them.
  const remembered = useRef<Record<number, readonly OpeningPeriod[]>>({});
  const firstOpen = week.findIndex((day) => day.length > 0);

  const put = (next: OpeningWeek): void => {
    setCopied(null);
    onWeek(next);
  };
  const setDay = (index: number, shift: readonly OpeningPeriod[]): void => {
    put(week.map((one, at) => (at === index ? shift : one)));
  };

  return (
    <div
      className="c-setup-card c-setup-week"
      id={id}
      role="group"
      aria-label={label}
      aria-describedby={describedBy}
      aria-invalid={invalid}
    >
      {timeZone === null ? null : (
        <span className="field c-setup-week-zone">
          <label htmlFor={`${id}-zone`}>{portalText("operations", "time_zone_eea79afd")}</label>
          <span className="c-setup-select">
            <select
              id={`${id}-zone`}
              className="input"
              value={timeZone}
              onChange={(event) => {
                onTimeZone?.(event.currentTarget.value);
              }}
            >
              {(onTimeZone === undefined ? [timeZone] : timeZoneOptionOf(timeZone)).map((zone) => (
                <option key={zone} value={zone}>
                  {timeZoneLabelOf(zone)}
                </option>
              ))}
            </select>
            <Icon glyph={GLYPH.caretDown} size="sm" />
          </span>
        </span>
      )}
      {copied === null ? null : (
        <p role="status" className="c-setup-copied">
          <Icon glyph={GLYPH.checkCircle} size="lg" />
          <span className="c-setup-copied-text">
            {copied.from}{portalText("operations", "s_hours_copied_to_84438f38")}{String(copied.count)} {portalText("operations", "other_open_day_68073bc7")}{copied.count === 1 ? "" : "s"}{portalText("operations", "closed_days_stay_closed_f848a3ea")}</span>
          <button
            type="button"
            className="btn btn-ghost c-setup-small-ghost"
            onClick={() => {
              onWeek(copied.previous);
              setCopied(null);
            }}
          >
            {portalText("operations", "undo_39fc7212")}</button>
        </p>
      )}
      <div className="c-setup-week-days">
        {DAY_NAME().map((day, index) => {
          const shift = week[index] ?? [];
          const open = shift.length > 0;
          return (
            <div key={index} className="c-setup-week-day">
              <span className="c-setup-week-name">{DAY_SHORT()[index]}</span>
              <button
                type="button"
                role="switch"
                aria-checked={open}
                aria-label={portalText("operations", "value_open_145d731e", { v0: String(day) })}
                className={open ? "c-setup-week-switch c-setup-week-switch-on" : "c-setup-week-switch"}
                onClick={() => {
                  if (open) {
                    remembered.current[index] = shift;
                    setDay(index, []);
                  } else {
                    setDay(index, remembered.current[index] ?? [BLANK_SHIFT]);
                  }
                }}
              >
                {open ? portalText("operations", "open_cf9b7706") : portalText("operations", "closed_88d86b77")}
              </button>
              <div className="c-setup-week-shifts">
                {open ? (
                  <>
                    {shift.map((one, at) => (
                      <span key={at} className="c-setup-week-shift">
                        <input
                          className="input c-setup-week-time"
                          type="text"
                          inputMode="numeric"
                          placeholder="HH:MM"
                          maxLength={5}
                          pattern="[0-2][0-9]:[0-5][0-9]"
                          aria-label={portalText("operations", "value_opensvalue_85fb35d7", { v0: String(day), v1: String(SHIFT_ORDINAL()[at] ?? "") })}
                          value={one.opensAt}
                          onChange={(event) => {
                            const opensAt = event.currentTarget.value;
                            setDay(index, shift.map((each, which) => (which === at ? { ...each, opensAt } : each)));
                          }}
                        />
                        <span className="c-setup-week-to">{portalText("operations", "to_4374aaee")}</span>
                        <input
                          className="input c-setup-week-time"
                          type="text"
                          inputMode="numeric"
                          placeholder="HH:MM"
                          maxLength={5}
                          pattern="[0-2][0-9]:[0-5][0-9]"
                          aria-label={portalText("operations", "value_closesvalue_c1b33bcd", { v0: String(day), v1: String(SHIFT_ORDINAL()[at] ?? "") })}
                          value={one.closesAt}
                          onChange={(event) => {
                            const closesAt = event.currentTarget.value;
                            setDay(index, shift.map((each, which) => (which === at ? { ...each, closesAt } : each)));
                          }}
                        />
                        <button
                          type="button"
                          className={shift.length > 1 ? "c-setup-week-remove" : "c-setup-week-remove c-setup-hidden"}
                          aria-label={portalText("operations", "remove_this_shift_66551c56")}
                          tabIndex={shift.length > 1 ? undefined : -1}
                          onClick={() => {
                            setDay(index, shift.filter((_each, which) => which !== at));
                          }}
                        >
                          <Icon glyph={GLYPH.x} size="sm" />
                        </button>
                      </span>
                    ))}
                    <span className="c-setup-week-actions">
                      {shift.length < MAX_SHIFT_PER_DAY ? (
                        <button
                          type="button"
                          className="btn btn-ghost c-setup-small-ghost"
                          onClick={() => {
                            const last = shift.at(-1);
                            setDay(index, [...shift, last === undefined || last.closesAt === "" ? BLANK_SHIFT : { opensAt: last.closesAt, closesAt: "" }]);
                          }}
                        >
                          <Icon glyph={GLYPH.plus} size="xs" />
                          {portalText("operations", "add_hours_c4a82938")}</button>
                      ) : null}
                      {index === firstOpen ? (
                        <button
                          type="button"
                          className="btn btn-ghost c-setup-small-ghost"
                          aria-label={portalText("operations", "copy_value_hours_to_every_open_day_950d7298", { v0: String(day) })}
                          onClick={() => {
                            const count = week.filter((other, at) => at !== index && other.length > 0).length;
                            const previous = week;
                            onWeek(week.map((other, at) => (at !== index && other.length > 0 ? shift : other)));
                            setCopied({ from: day, count, previous });
                          }}
                        >
                          <Icon glyph={GLYPH.copy} size="xs" />
                          {portalText("operations", "copy_to_all_open_days_07abc1f4")}</button>
                      ) : null}
                    </span>
                  </>
                ) : (
                  <span className="c-setup-week-closed">{portalText("operations", "closed_all_day_c09ef94b")}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {specialDate === undefined ? null : (
        <SpecialDateEditor
          title={portalText("operations", "merge.wizard.holidays_and_special_days.bad2a8")}
          entry={specialDate.entry}
          today={specialDate.today}
          disabled={false}
          onEntry={specialDate.onEntry}
        />
      )}
    </div>
  );
}

// ── special dates (plan-015 G4) ─────────────────────────────────────────────────────────────────

export interface SpecialDateEditorProps {
  /** "Holidays and special days" in Agent setup, "Special dates" on the agent page — the export words each. */
  readonly title: string;
  readonly entry: readonly SpecialDateEntry[];
  readonly today: string;
  /** For a person who may not change the agent: the chips without remove, the add row disabled. */
  readonly disabled: boolean;
  readonly onEntry: (entry: readonly SpecialDateEntry[]) => void;
}

/**
 * "Special dates" (`design/WeTalk.dc.html` 3253–3261 and 4261–4269, the `wh.holidays` block): a chip per date — or per
 * run of consecutive dates with the same hours — with remove, then the add row: the date as DD/MM/YYYY, "That day"
 * Closed or Different hours (one period, 12:00 to 15:00 to start), and "Add date". A date already listed is replaced.
 */
export function SpecialDateEditor({ title, entry, today, disabled, onEntry }: SpecialDateEditorProps): ReactNode {
  usePortalTranslation("operations");

  const [draft, setDraft] = useState<SpecialDateDraft>(FRESH_SPECIAL_DATE);
  const [problem, setProblem] = usePortalMessage();
  const radioName = `special-mode-${useId()}`;
  const chip = specialDateChipOf(entry);
  const put = (next: Partial<SpecialDateDraft>): void => {
    setProblem(null);
    setDraft((held) => ({ ...held, ...next }));
  };

  return (
    <div className="c-answer-special">
      <span className="c-answer-special-title">{title}</span>
      <span className="c-answer-special-dek">{portalText("operations", "merge.wizard.days_you_re_closed_or_open_different_hours.ed93be")}</span>

      {chip.length === 0 ? null : (
        <div className="c-answer-chips">
          {chip.map((one) => (
            <span
              key={one.date.join(",")}
              className="tag tag-neutral c-answer-chip"
              title={one.label === "" ? undefined : one.label}
            >
              {one.text}
              {disabled ? null : (
                <button
                  type="button"
                  className="c-answer-chip-remove"
                  aria-label={portalText("operations", "remove_value_f7ef029d", { v0: String(one.text) })}
                  onClick={() => {
                    onEntry(withoutSpecialDate(entry, one.date));
                  }}
                >
                  <Icon glyph={GLYPH.x} size="xs" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <span className="c-answer-add">
        <span className="c-answer-date">
          <input
            aria-label={portalText("operations", "date_eb9a4bc1")}
            className="input"
            type="text"
            inputMode="numeric"
            placeholder="DD/MM/YYYY"
            maxLength={10}
            value={draft.typed}
            disabled={disabled}
            onChange={(event) => {
              put({ typed: event.currentTarget.value });
            }}
          />
          <Icon glyph={GLYPH.calendarBlank} size="md" />
        </span>
        <span className="seg" role="radiogroup" aria-label={portalText("operations", "that_day_374eece4")}>
          {(
            [
              ["closed", portalText("operations", "closed_88d86b77")],
              ["hours", portalText("operations", "merge.wizard.different_hours.616f34")],

            ] as const
          ).map(([mode, label]) => (
            <label key={mode} className="seg-opt c-answer-nowrap">
              <input
                type="radio"
                name={radioName}
                checked={draft.mode === mode}
                disabled={disabled}
                onChange={() => {
                  put({ mode });
                }}
              />
              {label}
            </label>
          ))}
        </span>
        {draft.mode === "hours" ? (
          <>
            <input
              className="input c-answer-time"
              type="text"
              inputMode="numeric"
              placeholder="HH:MM"
              maxLength={5}
              pattern="[0-2][0-9]:[0-5][0-9]"
              aria-label={portalText("operations", "opens_54c374d4")}
              value={draft.opensAt}
              disabled={disabled}
              onChange={(event) => {
                put({ opensAt: event.currentTarget.value });
              }}
            />
            <span className="c-answer-to">{portalText("operations", "to_4374aaee")}</span>
            <input
              className="input c-answer-time"
              type="text"
              inputMode="numeric"
              placeholder="HH:MM"
              maxLength={5}
              pattern="[0-2][0-9]:[0-5][0-9]"
              aria-label={portalText("operations", "closes_ab16b985")}
              value={draft.closesAt}
              disabled={disabled}
              onChange={(event) => {
                put({ closesAt: event.currentTarget.value });
              }}
            />
          </>
        ) : null}
        <button
          type="button"
          className="btn btn-secondary"
          disabled={disabled}
          onClick={() => {
            const made = specialDateOfDraft(draft, entry, today);
            if ("problem" in made) {
              setProblem(() => (made.problem));
              return;
            }
            onEntry(withSpecialDate(entry, made.entry));
            setDraft((held) => ({ ...held, typed: "" }));
          }}
        >
          {portalText("operations", "add_date_3ae74694")}</button>
      </span>
      {problem === null ? null : (
        <span className="wt-field-error" role="alert">
          {problem}
        </span>
      )}
    </div>
  );
}

// ── out of hours and every line busy (plan-015 G8, G12) ─────────────────────────────────────────

export interface AnsweringChoiceGroupProps<T extends string> {
  /** The radio group's name and accessible label. */
  readonly name: string;
  readonly label: string;
  readonly option: readonly AnsweringOption<T>[];
  readonly chosen: T | null;
  /** Why "Forward to a phone" cannot be chosen now, or `null` when it can (plan-015 AP-5). */
  readonly forwardClosedReason: string | null;
  readonly disabled: boolean;
  readonly onChoose: (choice: T) => void;
}

/**
 * The design's radio cards (`hours.afterOpts` / `hours.busyOpts`): a label and the sentence of what happens. While
 * forwarding is closed its card is drawn greyed with the reason under it, as the prototype greys the checkout link.
 */
export function AnsweringChoiceGroup<T extends string>({
  name,
  label,
  option,
  chosen,
  forwardClosedReason,
  disabled,
  onChoose,
}: AnsweringChoiceGroupProps<T>): ReactNode {
  usePortalTranslation("operations");
  return (
    <div className="c-answer-choices" role="radiogroup" aria-label={label}>
      {option.map((one) => {
        const closed = one.choice === "forward" && forwardClosedReason !== null && chosen !== one.choice;
        return (
          <label key={one.choice} className={closed ? "c-answer-choice c-answer-choice-off" : "c-answer-choice"}>
            <input
              type="radio"
              name={name}
              value={one.choice}
              checked={chosen === one.choice}
              disabled={disabled || closed}
              onChange={() => {
                onChoose(one.choice);
              }}
            />
            <span className="c-answer-choice-body">
              <span className="c-answer-choice-label">{one.label}</span>
              <span className="c-answer-choice-dek">{one.dek}</span>
              {one.choice === "forward" && forwardClosedReason !== null ? (
                <span className="c-answer-choice-hint">{forwardClosedReason}</span>
              ) : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export interface LongestWaitFieldProps {
  readonly name: string;
  readonly minute: WaitMinute | null;
  /** The note under the choices: what callers hear, and after how long the agent takes a message. */
  readonly note: string;
  readonly disabled: boolean;
  readonly onMinute: (minute: WaitMinute) => void;
}

/** "Longest wait" (`hours.waits`): 1, 2, 3 or 5 minutes, and the sentence callers hear. */
export function LongestWaitField({ name, minute, note, disabled, onMinute }: LongestWaitFieldProps): ReactNode {
  usePortalTranslation("operations");
  return (
    <div className="field c-answer-wait">
      <span className="c-answer-wait-label" id={`${name}-label`}>
        {portalText("operations", "longest_wait_1cd850de")}</span>
      <span className="seg" role="radiogroup" aria-labelledby={`${name}-label`}>
        {WAIT_MINUTE_CHOICE.map((one) => (
          <label className="seg-opt c-answer-wait-opt" key={one}>
            <input
              type="radio"
              name={name}
              checked={minute === one}
              disabled={disabled}
              onChange={() => {
                onMinute(one);
              }}
            />
            {String(one)} {portalText("operations", "min_b6c935d4")}</label>
        ))}
      </span>
      <span className="c-answer-note">{note}</span>
    </div>
  );
}

export interface ForwardNumberFieldProps {
  readonly id: string;
  readonly value: string;
  readonly problem: string | null;
  /** "A forwarded call ends after 30 minutes…", or `null` where the limit is not served. */
  readonly limitSentence: string | null;
  readonly disabled: boolean;
  readonly onValue: (value: string) => void;
}

/** The phone a forward rings: a Greek number at launch (owner answers 14, 29), written as the person likes. */
export function ForwardNumberField({
  id,
  value,
  problem,
  limitSentence,
  disabled,
  onValue,
}: ForwardNumberFieldProps): ReactNode {
  usePortalTranslation("operations");
  return (
    <div className="field c-answer-forward">
      <label htmlFor={id}>{portalText("operations", "forward_to_44c59f80")}</label>
      <input
        id={id}
        className="input"
        type="tel"
        inputMode="tel"
        autoComplete="off"
        placeholder={FORWARD_EXAMPLE_NUMBER}
        value={value}
        disabled={disabled}
        aria-invalid={problem === null ? undefined : true}
        onChange={(event) => {
          onValue(event.currentTarget.value);
        }}
      />
      {limitSentence === null ? null : <span className="c-answer-note">{limitSentence}</span>}
      {problem === null ? null : (
        <span className="wt-field-error" role="alert">
          {problem}
        </span>
      )}
    </div>
  );
}
